import { Component, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  /** Label shown in the error UI and logged to console, e.g. "Quote Editor" */
  label?: string;
  /** Optional custom fallback UI. Receives a reset callback. */
  fallback?: (reset: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary
 *
 * Catches unhandled React render errors in any subtree.
 * Wraps high-risk surfaces: QuoteTemplate, Settings, RavanFAB.
 *
 * Usage:
 *   <ErrorBoundary label="Quote Editor">
 *     <QuoteTemplate ... />
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error(`[ErrorBoundary:${this.props.label ?? 'Unknown'}]`, error, info.componentStack);
    
    // Handle chunk loading / dynamic import errors gracefully by forcing a reload to get the latest app build.
    const isChunkError = 
      /Failed to fetch dynamically imported module/i.test(error.message) ||
      /chunk/i.test(error.message) ||
      /loading.*failed/i.test(error.message);
      
    if (isChunkError) {
      console.warn('[ErrorBoundary] Chunk load error detected. Reloading page to fetch latest deployment...');
      window.location.reload();
    }
  }

  reset = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.fallback) return this.props.fallback(this.reset);

    return (
      <div className="flex flex-col items-center justify-center p-12 gap-5 min-h-[200px]">
        <div className="h-12 w-12 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
          <AlertTriangle className="h-5 w-5 text-red-400" />
        </div>
        <div className="text-center space-y-1">
          <p className="text-sm font-semibold text-foreground uppercase tracking-widest">
            {this.props.label ?? 'This section'} encountered an error
          </p>
          {this.state.error?.message && (
            <p className="text-xs text-muted-foreground font-mono max-w-sm break-words">
              {this.state.error.message}
            </p>
          )}
        </div>
        <button
          onClick={this.reset}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600/10 hover:bg-emerald-600/20 border border-emerald-500/20 text-emerald-400 text-xs font-semibold uppercase tracking-widest transition-all"
        >
          <RefreshCw className="h-3 w-3" />
          Try again
        </button>
      </div>
    );
  }
}
